import {
  SpecialNeedsRenderer,
  SpecialNeedsValueGetter,
} from '@dataGrid/components/cellRenderer';
import {
  formatDateTime,
  formatDeliveryWindow,
  isValidTimeValue,
  mergeUserConfig,
} from '@dataGrid/gridUtilities';
import { COLUMN_CONFIG } from "../../../constants";
import appConstants from "./../../../../../../appConstants";
import {
  DateTimeFilter,
  MultiSelectFilter,
  StaticFilter,
} from '@dataGrid/components/columnFilters';
import { getShipmentCodes } from "../../../utils";
import { createCustomCheckboxColumn } from "@dataGrid/CustomGridCheckBox/createCustomCheckboxColumn";
import { CustomerNameCellRenderer } from "../../../../../common/CustomerMergeIndicator/CustomerNameCell";

export default class GridConfig {
  constructor(configuration) {
    const config = configuration || {};
    this.name = "GridConfiguration";
    this.config = GridConfig.getConfiguration(config);
  }
  
  static getConfiguration({ columnConfig, userConfig }) {
    const defaultConfig = GridConfig.getDefaultConfiguration({ columnConfig });
    return mergeUserConfig({
      defaultConfig,
      userConfig,
    });
  }

  static isFirstColumn(params) {
    const displayedColumns = params.columnApi.getAllDisplayedColumns();
    return displayedColumns[0] === params.column;
  }

  static getAPTWindowConfig(columnConfig) {
    if (columnConfig === COLUMN_CONFIG.SAIA) {
      return {
        headerName: "Delivery Window",
        headerTooltip: "Delivery Window",
        valueGetter: (params) =>
          params.data.shipmentCta || params.data.deliveryWindow.join(","),
        valueFormatter: formatDeliveryWindow,
      };
    }
    return {
      headerName: "APT Window",
      headerTooltip: "Appointment Window",
      valueGetter: (params) => params.data.shipmentCta,
      valueFormatter: (params) => {
        if (!isValidTimeValue(params.value)) {
          return;
        }
        return params.value;
      },
    };
  }

  static getSpecialNeedsOption(params) {
    return params.context.specialNeedsOptions;
  }

  static getProNumberClasses(params){
    return  ["gridCell-leftAlign", params.context.hasProImagesURL ?  "actionable-cell"  :""];
  }

  static getDefaultConfiguration({ columnConfig }) {
    const aptConfig = GridConfig.getAPTWindowConfig(columnConfig);

    const defaultConfig = {
      title: appConstants.gridNames.DueBillsGrid,
      keyColumn: "proId",
      rowSelection: "multiple",
      rowDeselection: true,
      defaultColDef: {
        sortable: true,
        resizable: true,
      },
      rowClassRules: {
        "ag-row-disabled": (params) => params.data.isInPlan,
      },
      columns: [
        createCustomCheckboxColumn("isInPlan"),
        {
          headerName: "Pro #",
          field: "proId",
          colId: "proId",
          filter: MultiSelectFilter,
          filterParams: {
            filterType: appConstants.FILTER_TYPES.STRING,
          },
          minWidth: appConstants.columnWidth.W_120,
          cellClass: GridConfig.getProNumberClasses,
        },
        {
          headerName: "Spl Needs",
          headerTooltip: "Special Needs",
          minWidth: appConstants.columnWidth.SPL_NEED,
          cellClass: "gridCell-leftAlign",
          valueGetter: SpecialNeedsValueGetter,
          filter: StaticFilter,
          filterParams: {
            getStaticOptions: GridConfig.getSpecialNeedsOption,
          },
          cellRendererParams: {
            getAPTWindow: aptConfig.valueGetter,
          },
          cellRenderer: SpecialNeedsRenderer,
        },
        {
          headerName: "Curr Term",
          headerTooltip: "Current Terminal",
          field: "currentTerminal",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_110,
        },
        {
          headerName: "Next Term",
          headerTooltip: "Next Terminal",
          field: "nextTerminal",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_110,
        },
        {
          headerName: "Dest Term",
          headerTooltip: "Destination Terminal",
          field: "destTerminal",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_110,
        },
        {
          headerName: "Orig Term",
          headerTooltip: "Origin Terminal",
          field: "originTerminal",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_110,
        },
        {
          headerName: "Due Date",
          headerTooltip: "Shipment Due Date",
          field: "shipmentDueDate",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_110,
        },
        {
          headerName: "Apt Due Date",
          headerTooltip: "Apt Due Date",
          field: "aptDueDate",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.APT_DUE_DATE,
        },
        {
          headerName: "Customer",
          headerTooltip: "Customer Name",
          field: "displayCustomerName",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.CUSTOMER,
          cellRenderer: CustomerNameCellRenderer,
        },
        {
          headerName: "Address",
          field: "customerAddress",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.CUSTOMER_ADDRESS,
          tooltipField: "customerAddress",
        },
        {
          headerName: "City",
          field: "customerCity",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.CITY,
        },
        {
          headerName: "State",
          field: "customerState",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_80,
        },
        {
          headerName: "Zip",
          field: "customerZip",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_85,
        },
        {
          headerName: aptConfig.headerName,
          headerTooltip: aptConfig.headerTooltip,
          field: "shipmentCTA",
          minWidth: appConstants.columnWidth.EARLY_LATE_CTA,
          cellClass: "gridCell-leftAlign",
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: appConstants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true,
          },
          valueGetter: aptConfig.valueGetter,
          valueFormatter: aptConfig.valueFormatter,
          tooltipValueGetter: (params) => params.value,
        },
        {
          headerName: "Delivery Window",
          headerTooltip: "Delivery Window",
          field: "deliveryWindow",
          minWidth: appConstants.columnWidth.DELIVERY_WINDOW,
          cellClass: "gridCell-leftAlign",
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: appConstants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true,
          },
          valueFormatter: formatDeliveryWindow,
        },
        {
          headerName: "HU",
          field: "hu",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_80,
        },
        {
          headerName: "Weight",
          field: "weight",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_95,
        },
        {
          headerName: "Pieces",
          field: "pieces",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_95,
        },
        {
          headerName: "Status",
          field: "shipmentStatus",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_120,
        },
        {
          headerName: 'Last Updated',
          headerTooltip: 'Last Updated',
          field: 'lastUpdated',
          minWidth: appConstants.columnWidth.IBETA,
          cellClass: 'gridCell-leftAlign',
          filter: DateTimeFilter,
          valueFormatter: formatDateTime,
          filterParams: {
            DATETYPE: appConstants.DATETYPES.DATETIME
          }
        },
        {
          headerName: "SVC",
          field: "priority",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_120,
        },
        {
          headerName: "Shipper Name",
          field: "shipperName",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.CUSTOMER,
        },
        {
          headerName: "Shipper Address",
          field: "shipperAddress",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.CUSTOMER_ADDRESS,
        },
        {
          headerName: "Shipper City",
          field: "shipperCity",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_130,
        },
        {
          headerName: "Shipper State",
          field: "shipperState",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_130,
        },
        {
          headerName: "Shipper Zip",
          field: "shipperZip",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_130
        },
        {
          headerName: "Shipment Codes",
          headerTooltip: "Shipment Codes",
          minWidth: appConstants.columnWidth.W_150,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,   
          valueGetter : getShipmentCodes,
          tooltipValueGetter: (params) => params.value
        },
        {
          headerName: "OSD Code",
          headerTooltip: "OSD Code",
          field: "osdCode",
          minWidth: appConstants.columnWidth.W_130,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter
        },
      ],
    };
    if (columnConfig === COLUMN_CONFIG.SAIA) {
      defaultConfig.columns = defaultConfig.columns.filter(
        (column) => column.field !== "deliveryWindow"
      );
    }
    return defaultConfig;
  }
}
